import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, FileText, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL, formatDateTime, PLAN_LABEL } from "@/lib/format";
import RatingDisplay from "@/components/RatingDisplay";
import CustomerCO2Widget from "@/components/CustomerCO2Widget";
import PullToRefreshContainer from "@/components/PullToRefreshContainer";
import PushNotificationsCard from "@/components/PushNotificationsCard";
import CustomerCreditCard from "@/components/CustomerCreditCard";
import ShareTrackingProfileCard from "@/components/ShareTrackingProfileCard";
import WhatsAppPreferencesCard from "@/components/WhatsAppPreferencesCard";
import SessionsCard from "@/components/SessionsCard";
import { formatCustomerAddress } from "@/lib/customerAddress";

const PLAN_TONE = {
  free: "bg-dapnow-bg text-dapnow-ink",
  business_start: "bg-dapnow-blue-soft text-dapnow-blue",
  business_pro: "bg-dapnow-blue text-white",
  enterprise: "bg-dapnow-ink text-white",
};

export default function CustomerProfile() {
  const { user, refresh } = useAuth();
  if (!user) return null;
  const plan = user.subscription_plan || "free";
  return (
    <PullToRefreshContainer onRefresh={async () => { await refresh(); toast.success("Profilo aggiornato"); }}>
    <div className="space-y-5" data-testid="customer-profile-page">
      <div>
        <p className="overline">Profilo</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Il tuo account</h1>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/app/subscription" data-testid="profile-subscription-link" className="card-soft p-5 hover:-translate-y-0.5 transition group">
          <div className="flex items-center justify-between">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Crown className="h-5 w-5" /></span>
            <ArrowRight className="h-4 w-4 text-dapnow-muted group-hover:translate-x-1 transition" />
          </div>
          <p className="overline mt-4">Abbonamento</p>
          <p className="font-display text-xl font-bold tracking-tight mt-0.5">{PLAN_LABEL[plan] || plan}</p>
          <span className={`mt-2 inline-flex pill ${PLAN_TONE[plan] || PLAN_TONE.free}`}>{plan === "free" ? "Gratuito" : "Attivo"}</span>
        </Link>
        <Link to="/app/invoices" data-testid="profile-invoices-link" className="card-soft p-5 hover:-translate-y-0.5 transition group">
          <div className="flex items-center justify-between">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><FileText className="h-5 w-5" /></span>
            <ArrowRight className="h-4 w-4 text-dapnow-muted group-hover:translate-x-1 transition" />
          </div>
          <p className="overline mt-4">Fatture</p>
          <p className="font-display text-xl font-bold tracking-tight mt-0.5">PDF download</p>
          <span className="mt-2 inline-flex pill bg-dapnow-bg text-dapnow-ink">Genera dopo il pagamento</span>
        </Link>
      </div>

      {/* Phase 44.12 — Credito DapNow wallet (referral CHF 5 + future) */}
      <CustomerCreditCard user={user} />

      {/* Phase 44.21 — Quick access "Condividi tracking" per ordini attivi */}
      <ShareTrackingProfileCard testid="customer-share-tracking-card" />

      {/* Phase 44.31 — Sicurezza · Sessioni attive */}
      <SessionsCard testid="customer-sessions-card" />

      <div className="card-soft p-6 sm:p-7 space-y-4">
        <Row k="Nome" v={user.name} />
        <Row k="Email" v={user.email} />
        <Row k="Telefono" v={user.phone || "—"} />
        <Row k="Tipo account" v={ROLE_LABEL[user.role]} />
        {user.business_name && <Row k="Ragione sociale" v={user.business_name} />}
        <Row k="Indirizzo" v={formatCustomerAddress(user.address)} />
        <Row k="Valutazione media" v={<RatingDisplay avg={user.avg_rating} count={user.ratings_count} />} />
        <Row k="Membro dal" v={formatDateTime(user.created_at)} />
      </div>

      <CustomerCO2Widget />

      <WhatsAppPreferencesCard user={user} refresh={refresh} />
      <PushNotificationsCard />
    </div>
    </PullToRefreshContainer>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between items-baseline gap-4 border-b border-dapnow-line pb-3 last:border-0 last:pb-0">
      <span className="overline text-[10px] shrink-0">{k}</span>
      <span className="font-medium text-right min-w-0 break-words">{v}</span>
    </div>
  );
}
